import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Card } from '@/components/ui/card';
import { 
  Calculator, 
  Plus, 
  Trash2, 
  Minus, 
  X, 
  Divide, 
  GripVertical, 
  Info 
} from 'lucide-react';
import type { CustomRatio } from '@creditnova/shared/schemas';


// ===========================================
// Types
// ===========================================

type FormulaElement = 
  | { type: 'field'; value: string; displayName: string }
  | { type: 'operator'; value: '+' | '-' | '*' | '/' }
  | { type: 'group'; elements: FormulaElement[] }
  | { type: 'number'; value: string }
  | { type: 'paren'; value: '(' | ')'; displayName: string };

interface FormulaBuilderProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableFields: string[];
  existingCustomRatios?: CustomRatio[];
  onSave: (ratio: Omit<CustomRatio, 'id'>) => void;
  onDelete?: (id: string) => void;
}

// ===========================================
// Common Financial Fields
// ===========================================

const commonFields = [
  // Assets
  'Total Assets',
  'Current Assets',
  'Cash and Cash Equivalents',
  'Accounts Receivable',
  'Inventory',
  'Fixed Assets',
  'Intangible Assets',
  
  // Liabilities
  'Total Liabilities',
  'Current Liabilities',
  'Long-term Debt',
  'Accounts Payable',
  
  // Equity
  'Total Equity',
  'Retained Earnings',
  'Common Stock',
  
  // Income
  'Revenue',
  'Net Sales',
  'Cost of Goods Sold',
  'Gross Profit',
  'Operating Income',
  'Net Income',
  'EBITDA',
  'EBIT',
  
  // Cash Flow
  'Operating Cash Flow',
  'Capital Expenditures',
  'Free Cash Flow',
  
  // Other
  'Interest Expense',
  'Depreciation',
  'Amortization',
];

// ===========================================
  // Validation Logic
  // ===========================================
  const validateFormula = (elements: FormulaElement[]): string | null => {
    if (elements.length === 0) return "Formula cannot be empty.";

    let openParenCount = 0;
    
    for (let i = 0; i < elements.length; i++) {
      const current = elements[i];
      const next = elements[i + 1];

      // 1. Parentheses Balance
      if (current.type === 'paren') {
        if (current.value === '(') openParenCount++;
        if (current.value === ')') openParenCount--;
        if (openParenCount < 0) return "Closing parenthesis found without a matching opening parenthesis.";
      }

      // 2. Operator Placement
      if (current.type === 'operator') {
        if (i === 0) return "Formula cannot start with an operator.";
        if (i === elements.length - 1) return "Formula cannot end with a trailing operator.";
        if (next && next.type === 'operator') return "Consecutive operators are not allowed.";
        if (next && next.type === 'paren' && next.value === ')') return "An operator cannot be immediately followed by a closing parenthesis.";
      }

      // 3. Logical Sequence (Implicit Multiplication Check)
      if (current.type === 'field' || current.type === 'number') {
        if (next && (next.type === 'field' || next.type === 'number')) {
          return `Missing operator between "${current.type === 'field' ? current.displayName : current.value}" and "${next.type === 'field' ? next.displayName : next.value}".`;
        }
        if (next && next.type === 'paren' && next.value === '(') {
          return "Please add an operator before an opening parenthesis.";
        }
      }

      // 4. Empty Parentheses
      if (current.type === 'paren' && current.value === '(' && next && next.type === 'paren' && next.value === ')') {
        return "Empty parentheses () are not allowed.";
      }
    }

    if (openParenCount !== 0) return `Unclosed parentheses. You have ${openParenCount} missing matching ")".`;

    return null;
  };

// ===========================================
// FormulaBuilder Main Component
// ===========================================

export function FormulaBuilder({
  open,
  onOpenChange,
  availableFields,
  existingCustomRatios = [],
  onSave,
  onDelete,
}: FormulaBuilderProps) {
  // Metadata State
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Formula Builder State
  const [formulaElements, setFormulaElements] = useState<FormulaElement[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedField, setDraggedField] = useState<string | null>(null);
  const [draggedElementIndex, setDraggedElementIndex] = useState<number | null>(null);

  // Combine and filter fields
  const allFields = [...new Set([...commonFields, ...availableFields])].sort().map(f => ({
    name: f,
    category: availableFields.includes(f) ? 'Extracted Data' : 'Common Field'
  }));

  const filteredFields = allFields.filter(field => 
    field.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Helper: Recursive string generation
  const generateFormulaString = (elements: FormulaElement[]): string => {
    return elements.map(el => {
      if (el.type === 'field') return el.value;
      if (el.type === 'operator') return ` ${el.value} `;
      if (el.type === 'number') return el.value;
      if (el.type === 'group') return `(${generateFormulaString(el.elements)})`;
      if (el.type === 'paren') return el.value;
      return '';
    }).join('');
  };

  const addElement = (el: FormulaElement) => {
    setFormulaElements(prev => {
      const newList = [...prev, el];
      // We don't block adding, but we update the error live
      setError(validateFormula(newList));
      return newList;
    });
  };

  // Logic: Adders
  const addField = (fieldName: string) => addElement({ type: 'field', value: fieldName, displayName: fieldName });
  const addOperator = (operator: '+' | '-' | '*' | '/') => addElement({ type: 'operator', value: operator });
  const addNumber = () => addElement({ type: 'number', value: '1.0' });
  const addParen = (value: '(' | ')') => addElement({ type: 'paren', value, displayName: value });

  const removeElement = (index: number) => {
    setFormulaElements(prev => {
      const newList = prev.filter((_, i) => i !== index);
      // Run validation immediately to clear/update error messages
      const vError = validateFormula(newList);
      setError(vError);
      return newList;
    });
  };

  const updateNumberValue = (index: number, value: string) => {
    setFormulaElements(prev => {
      const next = [...prev];
      if (next[index].type === 'number') {
        next[index] = { ...next[index], value } as FormulaElement;
      }
      return next;
    });
  };

  // Drag & Drop
  const handleDragStart = (fieldName: string) => setDraggedField(fieldName);


  // Start dragging from the workspace (to reposition)
  const handleWorkspaceDragStart = (index: number) => {
    setDraggedElementIndex(index);
    setDraggedField(null); // Clear sidebar drag state
  };

  const handleDropOnWorkspace = (e: React.DragEvent) => {
    e.preventDefault();
    
    // Dropping a new field from the sidebar
    if (draggedField) {
      addField(draggedField);
      setDraggedField(null);
      return;
    }

    // Repositioning an existing element within the workspace
    if (draggedElementIndex !== null) {
      repositionElement(draggedElementIndex, formulaElements.length);
      setDraggedElementIndex(null);
    }
  };

  const repositionElement = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    
    setFormulaElements(prev => {
      const newList = [...prev];
      const [movedItem] = newList.splice(fromIndex, 1);
      // Adjust toIndex if it was shifted by the splice
      const adjustedTo = toIndex > fromIndex ? toIndex - 1 : toIndex;
      newList.splice(adjustedTo, 0, movedItem);
      
      setError(validateFormula(newList));
      return newList;
    });
  };

  // Drop handler for individual elements (to drop BEFORE a specific item)
  const handleDropOnElement = (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedElementIndex !== null) {
      repositionElement(draggedElementIndex, targetIndex);
      setDraggedElementIndex(null);
    } else if (draggedField) {
      // Add new field at specific position
      setFormulaElements(prev => {
        const newList = [...prev];
        newList.splice(targetIndex, 0, { type: 'field', value: draggedField, displayName: draggedField });
        setDraggedField(null);
        setError(validateFormula(newList));
        return newList;
      });
    }
  };

  const handleSave = () => {
    if (!name.trim()) return setError('Please enter a ratio name');
    if (formulaElements.length === 0) return setError('Please build a formula');
    
   const validationError = validateFormula(formulaElements);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);

    onSave({
      name: name.trim(),
      formula: generateFormulaString(formulaElements),
      description: description.trim(),
    });

    handleClose();
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    setFormulaElements([]);
    setError(null);
    onOpenChange(false);
  };

  // Recursively render elements
  const renderFormulaElement = (element: FormulaElement, index: number) => {
    const isDragging = draggedElementIndex === index;

    const commonProps = {
      draggable: true,
      onDragStart: () => handleWorkspaceDragStart(index),
      onDragOver: (e: React.DragEvent) => e.preventDefault(),
      onDrop: (e: React.DragEvent) => handleDropOnElement(e, index),
      className: `cursor-grab active:cursor-grabbing transition-all ${isDragging ? 'opacity-20 scale-95' : 'opacity-100'}`,
    };

    const removeBtn = (
      <Button variant="ghost" size="sm" onClick={() => removeElement(index)} className="h-4 w-4 p-0 ml-1">
        <X className="w-3 h-3" />
      </Button>
    );

    if (element.type === 'field') return (
      <div key={`field-${index}`} {...commonProps}>
        <Badge variant="secondary" className="px-2 py-1 flex items-center gap-1 shadow-sm border-primary/10">
          <GripVertical className="w-2 h-2 text-muted-foreground/50" />
          {element.displayName} {removeBtn}
        </Badge>
      </div>
    );

    if (element.type === 'operator') return (
      <div key={`op-${index}`} {...commonProps}>
        <Badge variant="outline" className="px-2 py-1 flex items-center bg-background font-bold text-primary border-primary/20">
          <GripVertical className="w-2 h-2 text-muted-foreground/50 mr-1" />
          {element.value === '*' ? '×' : element.value === '/' ? '÷' : element.value} {removeBtn}
        </Badge>
      </div>
    );

    if (element.type === 'number') return (
      <div key={`num-${index}`} {...commonProps} className={`${commonProps.className} flex items-center gap-1 bg-muted rounded px-1 border shadow-inner`}>
        <GripVertical className="w-2 h-2 text-muted-foreground/50" />
        <Input 
          className="w-10 h-6 text-xs p-1 border-none bg-transparent focus-visible:ring-0" 
          value={element.value} 
          onChange={(e) => updateNumberValue(index, e.target.value)}
        />
        {removeBtn}
      </div>
    );

    if (element.type === 'paren') return (
      <div key={`paren-${index}`} {...commonProps}>
        <Badge variant="outline" className="px-3 py-1 font-black text-lg bg-primary/5 border-primary/30 flex items-center">
          <GripVertical className="w-2 h-2 text-muted-foreground/50 mr-1" />
          {element.value} {removeBtn}
        </Badge>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
     <DialogContent className="max-w-7xl h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5" />
            Custom Ratio Builder
          </DialogTitle>
          <DialogDescription>
            Create custom financial ratios using the available fields from your extracted data.
          </DialogDescription>
        </DialogHeader>

        <div className=" gap-6 flex-col flex overflow-hidden">
          {/* Metadata Panel */}
          <div className="col-span-3 space-y-4">
            <div className="space-y-">
              <Label>Ratio Name</Label>
              <Input placeholder="e.g. Acid Test Ratio" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input placeholder="What does this measure?" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </div>
          
          <div className="grid grid-cols-12 gap-6 flex-1 overflow-auto">
            {/* Existing Custom Ratios */}
            {existingCustomRatios.length > 0 && (
              <Card className="col-span-2 flex flex-col overflow-hidden h-full">
               <div className="mb-2 space-y-2 pl-4 pt-4">
                <Label className="text-sm font-semibold">Exiting Custom Ratios</Label>
               </div>
                <ScrollArea className="space-y-2 flex-1 pr-2 pl-2">
                  {existingCustomRatios.map((ratio) => (
                    <div 
                      key={ratio.id} 
                      className="flex items-center justify-between p-2 border rounded-lg bg-muted/30 mb-2"
                    >
                      <div>
                        <p className="font-medium text-sm">{ratio.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{ratio.formula}</p>
                      </div>
                      {onDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(ratio.id)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </ScrollArea>
              </Card>
            )}
            {/* Fields Selection Panel */}
            <Card className="col-span-2 p-4 flex flex-col overflow-hidden h-full">
              <div className="mb-4 space-y-2">
                <Label className="text-sm font-semibold">Available Fields</Label>
                <Input 
                  placeholder="Search metrics..." 
                  value={searchTerm} 
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-8"
                />
              </div>
              <ScrollArea className="flex-1 pr-2">
                <div className="space-y-1">
                  {filteredFields.map((field, i) => (
                    <div
                      key={i}
                      draggable
                      onDragStart={() => handleDragStart(field.name)}
                      onClick={() => addField(field.name)}
                      className="flex items-center gap-2 p-2 rounded border border-transparent hover:border-primary/20 hover:bg-primary/5 cursor-move group transition-all"
                    >
                      <GripVertical className="w-3 h-3 text-muted-foreground group-hover:text-primary" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{field.name}</p>
                        <p className="text-[10px] text-muted-foreground">{field.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </Card>

            {/* Workspace Panel */}
            <div className={`flex flex-col gap-4 overflow-hidden ${existingCustomRatios.length > 0 ? 'col-span-8' : 'col-span-10'}`}>
              <Card 
                className="flex-1 p-6 border-2 border-dashed bg-muted/10 relative"
                onDragOver={(e) => e.preventDefault()}
                // onDrop={handleDrop}
                onDrop={handleDropOnWorkspace}
              >
                <Label className="absolute top-2 left-3 text-[10px] text-muted-foreground font-mono uppercase">Workspace</Label>
                <div className="flex flex-wrap items-center gap-2 content-start h-full pt-4">
                  {formulaElements.length === 0 ? (
                    <div className="flex flex-col items-center justify-center w-full h-full text-center text-muted-foreground">
                      <Info className="w-8 h-8 mb-2 opacity-20" />
                      <p className="text-xs">Drag fields here or click items to build your ratio</p>
                    </div>
                  ) : (
                    formulaElements.map((el, i) => renderFormulaElement(el, i))
                  )}
                </div>
              </Card>

              <Card className="p-4 bg-muted/30">
                <Label className="text-xs font-semibold mb-3 block text-muted-foreground">Operators & Tools</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex gap-1.5 flex-wrap">
                    {[
                      { icon: Plus, val: '+' as const },
                      { icon: Minus, val: '-' as const },
                      { icon: X, val: '*' as const },
                      { icon: Divide, val: '/' as const },
                    ].map(op => (
                      <Button key={op.val} variant="outline" size="icon" className="h-8 w-8" onClick={() => addOperator(op.val)}>
                        <op.icon className="h-4 w-4" />
                      </Button>
                    ))}
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="h-8 w-8 font-bold text-sm" 
                      onClick={() => addParen('(')}
                    >
                      (
                    </Button>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="h-8 w-8 font-bold text-sm" 
                      onClick={() => addParen(')')}
                    >
                      )
                    </Button>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" size="sm" className="h-8 text-[10px]" onClick={addNumber}>
                      ADD CONSTANT
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 text-destructive text-[10px]" onClick={() => setFormulaElements([])}>
                      CLEAR
                    </Button>
                  </div>
                </div>
              </Card>

              <div className="p-3 bg-primary/5 rounded border border-primary/10">
                <p className="text-[10px] text-primary font-bold uppercase mb-1">Live Formula String</p>
                <code className="text-sm font-mono break-all text-foreground">
                  {generateFormulaString(formulaElements) || '...'}
                </code>
              </div>
              {error && <p className="text-xs text-destructive font-medium">{error}</p>}
            </div>
          </div>

          
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} className="px-8">
            <Plus className="w-4 h-4 mr-2" />
            Add Custom Ratio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

